'use client';

import { createClient } from '@/lib/supabase/client';

const BUCKET = 'game-images';
const FOLDER = 'distractions';

export interface DistractionImage {
  id: string;
  imageUrl: string;
  storagePath: string;
}

export const distractionImageService = {
  async getAll(): Promise<DistractionImage[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('distraction_images')
      .select('*')
      .order('created_at', { ascending: true });

    if (error || !data) return [];

    return data.map((row) => {
      const storedPath: string = row.storage_path;
      let imageUrl: string;
      if (storedPath.startsWith('http')) {
        imageUrl = `${storedPath.split('?')[0]}?cb=${Date.now()}`;
      } else {
        const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storedPath);
        imageUrl = `${urlData.publicUrl}?cb=${Date.now()}`;
      }
      return { id: row.id, imageUrl, storagePath: storedPath };
    });
  },

  async upload(file: File): Promise<DistractionImage> {
    const supabase = createClient();
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
    const uniqueName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const storagePath = `${FOLDER}/${uniqueName}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, file, {
        upsert: false,
        cacheControl: '0',
        contentType: file.type,
      });

    if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
    let imageUrl = `${urlData.publicUrl}?cb=${Date.now()}`;

    const { data: row, error: dbError } = await supabase
      .from('distraction_images')
      .insert({ image_url: urlData.publicUrl, storage_path: storagePath })
      .select('id')
      .single();

    if (dbError) throw new Error(`DB save failed: ${dbError.message}`);

    return { id: row.id, imageUrl, storagePath };
  },

  async remove(id: string, storagePath: string): Promise<void> {
    const supabase = createClient();
    await supabase.storage.from(BUCKET).remove([storagePath]);
    await supabase.from('distraction_images').delete().eq('id', id);
  },
};
